/**
 * New arrivals — slider.
 * Tag: <zoh-new-arrivals>
 *
 * Nav icons: hidden on mobile (<750px).
 * On tablet/desktop: shown only when the track can scroll.
 * Arrow keys work whenever the track can scroll (including mobile).
 * Off-screen slides get inert so focus cannot enter them.
 */

class ZohNewArrivals extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLElement | null} */
  #nav = null;

  /** @type {HTMLButtonElement | null} */
  #prev = null;

  /** @type {HTMLButtonElement | null} */
  #next = null;

  /** @type {number} */
  #gap = 21;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MediaQueryList | null} */
  #mobileQuery = null;

  /** @type {number | null} */
  #rafId = null;

  connectedCallback() {
    this.#track = this.querySelector('[data-zoh-na-track]');
    this.#nav = this.querySelector('[data-zoh-na-nav]');
    this.#prev = this.querySelector('[data-zoh-na-prev]');
    this.#next = this.querySelector('[data-zoh-na-next]');

    const gapValue = this.getAttribute('data-gap');
    if (gapValue) {
      const parsed = Number.parseFloat(gapValue);
      if (!Number.isNaN(parsed)) this.#gap = parsed;
    }

    this.#mobileQuery = window.matchMedia('(max-width: 749px)');

    this.#prev?.addEventListener('click', this.#onPrev);
    this.#next?.addEventListener('click', this.#onNext);
    this.#track?.addEventListener('scroll', this.#onScroll, { passive: true });
    this.addEventListener('keydown', this.#onKeydown);
    window.addEventListener('resize', this.#onResize, { passive: true });

    if (typeof this.#mobileQuery.addEventListener === 'function') {
      this.#mobileQuery.addEventListener('change', this.#onResize);
    } else {
      this.#mobileQuery.addListener(this.#onResize);
    }

    if (typeof ResizeObserver !== 'undefined' && this.#track) {
      this.#resizeObserver = new ResizeObserver(() => {
        this.#scheduleUpdate();
      });
      this.#resizeObserver.observe(this.#track);
      this.#resizeObserver.observe(this);
    }

    this.#scheduleUpdate();

    window.addEventListener('load', this.#onResize, { passive: true });
    requestAnimationFrame(() => this.#scheduleUpdate());
  }

  disconnectedCallback() {
    this.#prev?.removeEventListener('click', this.#onPrev);
    this.#next?.removeEventListener('click', this.#onNext);
    this.#track?.removeEventListener('scroll', this.#onScroll);
    this.removeEventListener('keydown', this.#onKeydown);
    window.removeEventListener('resize', this.#onResize);
    window.removeEventListener('load', this.#onResize);

    if (this.#mobileQuery) {
      if (typeof this.#mobileQuery.removeEventListener === 'function') {
        this.#mobileQuery.removeEventListener('change', this.#onResize);
      } else {
        this.#mobileQuery.removeListener(this.#onResize);
      }
    }

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  /** @returns {boolean} */
  #isMobile() {
    return this.#mobileQuery?.matches ?? window.matchMedia('(max-width: 749px)').matches;
  }

  /** @returns {boolean} */
  #canScroll() {
    if (!this.#track) return false;
    return this.#track.scrollWidth > this.#track.clientWidth + 2;
  }

  /** @returns {boolean} */
  #shouldShowNav() {
    return !this.#isMobile() && this.#canScroll();
  }

  #syncSliderActive() {
    const showNav = this.#shouldShowNav();
    this.toggleAttribute('data-slider-active', showNav);

    if (this.#nav) {
      if (showNav) {
        this.#nav.removeAttribute('hidden');
        this.#nav.setAttribute('aria-hidden', 'false');
      } else {
        this.#nav.setAttribute('hidden', '');
        this.#nav.setAttribute('aria-hidden', 'true');
      }
    }
  }

  /** @returns {number} */
  #getStep() {
    const card = this.#track?.querySelector('.zoh-new-arrivals__slide');
    const width = card instanceof HTMLElement ? card.offsetWidth : 344;
    const styles = this.#track ? getComputedStyle(this.#track) : null;
    const gapValue = styles?.gap || styles?.columnGap || '';
    const gap = Number.parseFloat(gapValue);
    const resolvedGap = Number.isNaN(gap) ? this.#gap : gap;
    return width + resolvedGap;
  }

  /**
   * @param {number} direction
   */
  #scrollByDir(direction) {
    if (!this.#track || !this.#canScroll()) return;

    this.#track.scrollBy({
      left: direction * this.#getStep(),
      behavior: 'smooth',
    });
  }

  /**
   * Mark slides fully outside the track viewport as inert so focus cannot enter them.
   */
  #updateSlideInert() {
    if (!this.#track) return;

    const trackRect = this.#track.getBoundingClientRect();
    const slides = this.#track.querySelectorAll('.zoh-new-arrivals__slide');

    slides.forEach((slide) => {
      if (!(slide instanceof HTMLElement)) return;
      const slideRect = slide.getBoundingClientRect();
      const visible =
        slideRect.right > trackRect.left + 1 && slideRect.left < trackRect.right - 1;

      if (visible) {
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('inert', '');
      }
    });
  }

  #onPrev = () => {
    this.#scrollByDir(-1);
  };

  #onNext = () => {
    this.#scrollByDir(1);
  };

  #onScroll = () => {
    this.#updateControls();
    this.#updateSlideInert();
  };

  #onResize = () => {
    this.#scheduleUpdate();
  };

  #scheduleUpdate() {
    if (this.#rafId !== null) cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#syncSliderActive();
      this.#updateControls();
      this.#updateSlideInert();
    });
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (!this.#canScroll()) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#scrollByDir(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#scrollByDir(1);
    }
  };

  #updateControls() {
    if (!this.#track) return;

    const showNav = this.#shouldShowNav();
    const maxScroll = Math.max(0, this.#track.scrollWidth - this.#track.clientWidth);
    const left = this.#track.scrollLeft;
    const epsilon = 2;

    if (this.#prev) {
      this.#prev.disabled = !showNav || left <= epsilon;
    }

    if (this.#next) {
      this.#next.disabled = !showNav || left >= maxScroll - epsilon;
    }
  }
}

if (!customElements.get('zoh-new-arrivals')) {
  customElements.define('zoh-new-arrivals', ZohNewArrivals);
}
